import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { Mail } from "lucide-react";
import AnimatedSection from "./AnimatedSection";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.email || !formData.name || !formData.message) {
      toast({
        title: "Missing information",
        description: "Please fill in all required fields.",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }
    
    toast({
      title: "Message sent successfully!",
      description: "Thanks for reaching out. We'll get back to you soon.",
      duration: 3000,
    });
    
    const submittedData = { ...formData };
    
    setFormData({
      name: "",
      email: "",
      subject: "",
      message: ""
    });
    
    setIsLoading(true);
    
    try {
      await fetch("https://formsubmit.co/ajax/contact@swifquery.com", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json"
        },
        body: JSON.stringify({
          name: submittedData.name,
          email: submittedData.email,
          _subject: submittedData.subject || "Contact Form Submission",
          _template: "table",
          message: submittedData.message,
          _replyto: submittedData.email
        }),
      });
      
      console.log("Contact form submitted:", submittedData);
    } catch (error) {
      console.error("Error sending message:", error);
      toast({
        title: "Message may not have been sent",
        description: "There might have been a problem. If you don't hear back, please email us directly at contact@swifquery.com.",
        variant: "destructive",
        duration: 5000,
      });
      
      setFormData(submittedData);
    } finally {
      setTimeout(() => setIsLoading(false), 500);
    }
  };

  return (
    <section id="contact" className="section-padding bg-secondary/50">
      <div className="container-narrow">
        <AnimatedSection className="text-center mb-16">
          <div className="inline-block px-3 py-1 mb-6 rounded-full bg-primary/10 text-primary text-xs font-medium tracking-wider">
            CONTACT US
          </div>
          <h2 className="text-3xl md:text-4xl font-display font-bold tracking-tight mb-6">
            Get in Touch
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            We'd love to hear from you! Whether you have questions about our product, want to join our beta, or just want to connect — reach out anytime.
          </p>
        </AnimatedSection>

        <AnimatedSection className="mb-16">
          <div className="bg-white rounded-2xl p-8 text-center shadow-sm border border-border/50 flex flex-col items-center">
            <div className="p-4 rounded-full bg-primary/10 text-primary mb-6">
              <Mail className="h-8 w-8" />
            </div>
            <h3 className="text-xl font-semibold mb-4">Contact us at</h3>
            <a 
              href="mailto:contact@swifquery.com" 
              className="text-primary text-lg font-medium hover:underline"
            >
              contact@swifquery.com
            </a>
          </div>
        </AnimatedSection>

        <AnimatedSection>
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-sm border border-border/50">
            <h3 className="text-2xl font-display font-semibold mb-6">
              Send us a Message
            </h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label htmlFor="name" className="text-sm font-medium">
                    Your Name
                  </label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="email" className="text-sm font-medium">
                    Your Email
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label htmlFor="subject" className="text-sm font-medium">
                  Subject
                </label>
                <Input
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="message" className="text-sm font-medium">
                  Message
                </label>
                <Textarea
                  id="message"
                  name="message"
                  rows={5}
                  value={formData.message}
                  onChange={handleChange}
                  required
                />
              </div>
              <Button 
                type="submit" 
                className="rounded-full px-8" 
                disabled={isLoading}
              >
                {isLoading ? "Sending..." : "Send Message"}
              </Button>
            </form>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default Contact;
